import {Popover} from '@mui/material';
import {ConfigObjectType, LightIconButton} from '@wallaby-ui/core';
import React, {useMemo, useState} from 'react';

import {FILTER_ACTIVE_ICON_COLORS, LightSavedFiltersBox, toComparableString, useUserFilters} from './LightSavedFiltersBox';

export type LightSavedFiltersButtonProps = {
  storageIdPrefix: string;
  component: string;
  config: ConfigObjectType;
  filter: Record<string, any>;
  setFilter: (newFilter: Record<string, any>) => void;
  normalizeForCompare?: (filter: Record<string, any>) => Record<string, any>;
  normalizeForStore?: (filter: Record<string, any>) => Record<string, any>;
};

/**
 * A star icon button that opens a floating popover with saved filters management.
 * Wraps LightSavedFiltersBox and useUserFilters — drop it into any component that has
 * a `filter` object and a `setFilter` callback to get full saved-filter support.
 *
 * Usage:
 *   <LightSavedFiltersButton
 *     storageIdPrefix='/myPage.mySection'
 *     component='myComponentIdentifier'
 *     config={config}
 *     filter={currentFilters}
 *     setFilter={(newFilter) => setFilters(newFilter as MyFilterType)}
 *   />
 */
export const LightSavedFiltersButton: React.FC<LightSavedFiltersButtonProps> = (props) => {
  const {storageIdPrefix, component, config, filter, setFilter, normalizeForCompare, normalizeForStore} = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const {userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: setFilter,
  });

  const filterString = useMemo(() => toComparableString(filter ?? {}, normalizeForCompare), [filter, normalizeForCompare]);
  const allUserFilters = useMemo(() => [...addedUserFilters, ...userFilters], [addedUserFilters, userFilters]);
  const hasSavedFilterSelected = useMemo(
    () =>
      allUserFilters.some((f) => {
        try {
          return toComparableString(JSON.parse(f.data), normalizeForCompare) === filterString;
        } catch {
          return false;
        }
      }),
    [allUserFilters, filterString, normalizeForCompare]
  );

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <LightIconButton
        iconName='savedFiltersIconMultiColor'
        title='filter.user.savedFilters'
        selected={open}
        onClick={handleToggle}
        wrapperStyle={open ? ({'--dlMultiColorSecondary': 'var(--dlLightIconButtonSelectedFill)'} as React.CSSProperties) : undefined}
        colors={hasSavedFilterSelected && !open ? FILTER_ACTIVE_ICON_COLORS : undefined}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        disableRestoreFocus
        PaperProps={{style: {border: '1px solid var(--dlLightIconButtonSelectedBackground)'}}}
      >
        <div style={{padding: '10px 8px 0 8px', width: 308}}>
          <LightSavedFiltersBox
            isPopupMode
            open={false}
            toggleOpen={handleClose}
            storageIdPrefix={storageIdPrefix}
            component={component}
            userFiltersLoading={userFiltersLoading}
            addedUserFilters={addedUserFilters}
            userFilters={userFilters}
            changeUserFilter={changeUserFilter}
            filter={filter}
            setFilter={setFilter}
            config={config}
            normalizeForCompare={normalizeForCompare}
            normalizeForStore={normalizeForStore}
            savedFiltersScrollBoxStyles={{maxHeight: 320, overflowY: 'auto'}}
          />
        </div>
      </Popover>
    </>
  );
};
